import { type VariantProps, cva } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../cn";

const switchTrackVariants = cva(
  // `group` so the thumb can react to the track's `data-state`. The track is a
  // plain CSS-transition toggle (no framer) so it needs no measurement and the
  // global reduced-motion reset in globals.css disables the movement for users
  // who ask for it.
  "group inline-flex shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted",
  {
    variants: {
      size: {
        sm: "h-5 w-9",
        md: "h-6 w-11",
      },
    },
    defaultVariants: { size: "md" },
  },
);

const switchThumbVariants = cva(
  "pointer-events-none block rounded-full bg-surface shadow-sm ring-1 ring-border/60 transition-transform duration-200",
  {
    variants: {
      size: {
        // Travel = track width − thumb width − 2×inset, expressed as the checked
        // translate so the thumb lands flush against the far edge.
        sm: "size-4 translate-x-0.5 group-data-[state=checked]:translate-x-[1.125rem]",
        md: "size-5 translate-x-0.5 group-data-[state=checked]:translate-x-[1.375rem]",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface SwitchProps
  extends Omit<
      ButtonHTMLAttributes<HTMLButtonElement>,
      "type" | "onChange" | "value" | "children"
    >,
    VariantProps<typeof switchTrackVariants> {
  /** Controlled on/off state; drives both the visual and `aria-checked`. */
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
}

/**
 * Controlled boolean toggle rendered as a WAI-ARIA `switch`. Presentational and
 * form-agnostic like `Input`/`Segmented`: it owns only the toggle affordance and
 * accessibility wiring, leaving validation/state to the caller. Injected
 * `id`/`aria-*` props (e.g. from the `Field` render-prop) flow through `...props`.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    { className, checked, onCheckedChange, size, disabled, onClick, ...props },
    ref,
  ) => (
    <button
      ref={ref}
      {...props}
      type="button"
      role="switch"
      aria-checked={checked}
      data-state={checked ? "checked" : "unchecked"}
      disabled={disabled}
      // Compose the caller's handler first so it can `preventDefault()` to veto
      // the toggle (e.g. guarding an unsaved-changes confirm) before we flip.
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          onCheckedChange(!checked);
        }
      }}
      className={cn(switchTrackVariants({ size }), className)}
    >
      <span className={switchThumbVariants({ size })} aria-hidden="true" />
    </button>
  ),
);
Switch.displayName = "Switch";

export { switchTrackVariants };
